/**
 * The answers the top of `/bills` gives before anything else (2026-10-03).
 *
 * Victor, on the page as it was: "I am expecting to see the list of my grants
 * and the status — everything good? Do I need any action? Where is my money?"
 * Each export below answers one of those, from data the page already loads
 * (`GET /grants`, `GET /reimbursements/mine`). Pure, so the rules are tested
 * here and the components only render them.
 */
import { needsAttention } from '@/lib/bills-display';
import type { GrantCurrency, GrantRequest, ReimbursementClaim } from '@/types/api';

/** 'YYYY-MM' ± n months. */
export function addMonths(ym: string, n: number): string {
  const [y, m] = ym.split('-').map(Number);
  const idx = y * 12 + (m - 1) + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

/** The reader's own calendar month, as 'YYYY-MM'. */
function monthOf(now: Date): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The UTC calendar month, as 'YYYY-MM'. The renewal window reads months in
 * UTC so it agrees with the server's copy of the rule
 * (backend/src/helpers/renewalCycle.js), which decides whether
 * `POST /api/renewals` starts a new draft. In the reader's local month the
 * two disagreed for a few hours at a month boundary (#2676).
 */
function utcMonthOf(now: Date): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Approved (or fulfilled) Signature grants that carry a dated, monthly schedule. */
function datedSignatures(grants: GrantRequest[]) {
  return grants.flatMap((g) => {
    const s = g.award?.schedule;
    if (g.type !== 'signature' || (g.status !== 'approved' && g.status !== 'fulfilled')) return [];
    if (!s || !s.start_month || !MONTH_RE.test(s.start_month) || !(s.months > 0)) return [];
    return [{ grant: g, schedule: s as typeof s & { start_month: string } }];
  });
}

/** #2687: the last program year. A Signature grant renews for up to 3 years. */
export const FINAL_GRANT_YEAR = 3;

/**
 * Whether the family should be invited to renew: the final 3 months of an
 * approved Signature grant, and every month after it ran out (Victor's rule,
 * 2026-10-03). A 12-month grant starting January opens in October.
 *
 * Closed while payments are paused, and closed when the start month is
 * unknown — an invitation the platform cannot date is the "reminded every
 * time the page loads" defect this replaces. Closed for a Year 3 grant:
 * renewals stop at Year 3 (#2687).
 */
export function signatureRenewalDue(grants: GrantRequest[], now: Date = new Date()): boolean {
  // 2026-10-11 (J2 c02): a Signature request already in flight IS the next
  // year's ask — a committee-approved renewal's grant sits in `submitted`
  // until the desk approves it (DECISIONS 2026-10-05).
  if (grants.some((g) => g.type === 'signature' && SIGNATURE_IN_FLIGHT.has(g.status))) return false;
  // The window belongs to the LATEST Signature grant. Any grant in its final
  // months used to open it, so a family already awarded next year's grant was
  // still invited to renew on /bills and Home.
  const latest = datedSignatures(grants).reduce<{ end: string; paused: boolean; createdAt?: string; year?: number | null } | null>((best, { grant, schedule }) => {
    const end = addMonths(schedule.start_month, schedule.months);
    return !best || end > best.end
      ? { end, paused: Boolean(schedule.paused), createdAt: grant.created_at, year: grant.grant_year }
      : best;
  }, null);
  if (!latest || latest.paused) return false;
  // #2687 (Victor, 2026-10-14): renewals stop at Year 3 (trf.org/get-support).
  // A Year 3 grant is the last, so it opens no invitation and no due marker.
  // An unknown year caps nothing. Mirrors backend helpers/renewalCycle.js.
  if (typeof latest.year === 'number' && latest.year >= FINAL_GRANT_YEAR) return false;
  // #2533: a NEWER approved Signature grant whose schedule is not dated yet
  // (approved, start month still to be set) already covers the next period.
  // It never enters the reduce above, so the old grant's final months kept
  // the invitation open beneath it.
  if (latest.createdAt && grants.some((g) =>
    g.type === 'signature'
    && (g.status === 'approved' || g.status === 'fulfilled')
    && !datedSignatures([g]).length
    && typeof g.created_at === 'string'
    && g.created_at > latest.createdAt!,
  )) return false;
  return utcMonthOf(now) >= addMonths(latest.end, -3);
}

const SIGNATURE_IN_FLIGHT = new Set<string>(['draft', 'submitted', 'under_review']);

export type NextPayment =
  | { kind: 'month'; ym: string }
  | { kind: 'due'; ym: string }
  | { kind: 'paused' };

/**
 * The family's next Signature payment: a grant's start month plus the
 * payments already recorded. A month already behind us is `due`, and still
 * carries its month: "due" alone left the family with a line that had no
 * date in it (#2334), so the line says "due since August 2026". Null when
 * every payment is made or the schedule is undated.
 *
 * With more than one live Signature grant (#2343), the answer is the payment
 * that comes next across all of them: the earliest month among the grants
 * still paying, so an overdue payment outranks an upcoming one. A paused
 * grant reports `paused` only when no other live grant is paying. Array
 * order never decides; before, the first live grant in the list did.
 *
 * ZERO RECORDED PAYMENTS IS UNKNOWN, NOT LATE (D-2394 (a), Victor,
 * 2026-10-09). Signature payments made through Bill.com are never written
 * back to `award.payments`, and the production backfill wrote none, so a
 * grant that started in December with nothing recorded has very likely been
 * paid. Calling it "due since December 2025" beside "All on track" was a
 * false statement to a family. Such a grant names no payment at all: it is
 * left out of the earliest-month race, and it also keeps a paused sibling
 * from speaking for every grant. A zero-payment grant whose start is this
 * month or later still names its start month, because nothing is late yet.
 * Revisit once #446/#994 record payments.
 */
export function nextSignaturePayment(grants: GrantRequest[], now: Date = new Date()): NextPayment | null {
  const current = monthOf(now);
  const live = datedSignatures(grants).filter(({ schedule }) => (schedule.paid_count ?? 0) < schedule.months);
  const unknown = ({ schedule }: (typeof live)[number]) =>
    !schedule.paused && (schedule.paid_count ?? 0) === 0 && schedule.start_month < current;
  const known = live.filter((g) => !unknown(g));
  if (!known.length) return null;
  const months = known
    .filter(({ schedule }) => !schedule.paused)
    .map(({ schedule }) => addMonths(schedule.start_month, schedule.paid_count ?? 0))
    .sort();
  if (!months.length) return known.length === live.length ? { kind: 'paused' } : null;
  const ym = months[0];
  return ym < current ? { kind: 'due', ym } : { kind: 'month', ym };
}

/**
 * Money TRF has actually paid the family: recorded Signature payments plus
 * receipts paid back. Approved-but-unpaid is not "paid", so it is not here.
 * Receipt amounts are dollars on the claim wire; every award in another
 * currency makes the sum meaningless, so it answers null rather than adding
 * CA$ to US$.
 */
export function paidToFamily(
  grants: GrantRequest[],
  claims: ReimbursementClaim[],
): { cents: number; currency: GrantCurrency } | null {
  if (grants.some((g) => g.award && g.award.currency !== 'USD')) return null;
  const payments = grants.reduce((sum, g) => {
    const s = g.award?.schedule;
    return s ? sum + (s.paid_count ?? 0) * s.monthly_cents : sum;
  }, 0);
  const receipts = claims
    .filter((c) => c.status === 'paid' && c.amount != null && Number.isFinite(Number(c.amount)))
    .reduce((sum, c) => sum + Math.round(Number(c.amount) * 100), 0);
  return { cents: payments + receipts, currency: 'USD' };
}

/**
 * Per grant, how many of its linked receipts are waiting on the family.
 * Before 2026-10-03 these were visible only inside an expanded grant.
 */
export function todoCountByGrant(claims: ReimbursementClaim[], now: Date = new Date()): Map<string, number> {
  const counts = new Map<string, number>();
  for (const c of claims) {
    if (!c.linked_to_grant || !needsAttention(c, now)) continue;
    counts.set(c.linked_to_grant, (counts.get(c.linked_to_grant) ?? 0) + 1);
  }
  return counts;
}

/**
 * A request the family withdrew: folded into "Past grant requests" below the
 * live ones. A DENIED request is not here (#2533): it was never a grant, so it
 * sits in its own "Not approved" group — see `isNotApprovedGrant`.
 */
export function isPastGrant(grant: Pick<GrantRequest, 'status'>): boolean {
  return grant.status === 'withdrawn';
}

/** A request TRF did not approve: its own small "Not approved" group on /bills. */
export function isNotApprovedGrant(grant: Pick<GrantRequest, 'status'>): boolean {
  return grant.status === 'denied';
}
